/**
 * Tờ hỏi lại "Lưu xong, tiệm sẽ không nhận được khách" (v0.22.3 B5, ADR-122). MỘT hành động chính: "Thôi, để như cũ" (an toàn);
 * "Vẫn lưu" là nút phụ. Dùng ở lưu nhân viên / lưu ca / cho nghỉ việc / tắt dịch vụ / lưu giờ mở cửa qua `useHoiNgungNhan`.
 *
 * Vòng sửa 1 v0.23.1 (G-N1): bấm "Vẫn lưu" tờ KHÔNG đóng — hai nút khoá, "Vẫn lưu" đổi thành "Đang lưu…" tới khi máy chủ lưu xong; chỉ
 * đóng khi `chay` kết thúc. Không có nút X: "Thôi, để như cũ" (và Esc / nền) là lối thoát.
 */
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { cauHoiNgungNhan, type KetQuaLuu, luuCoHoiLai } from './hoi-ngung-nhan.ts'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

export function HoiNgungNhan({
  mat,
  dangLuu,
  onVanLuu,
  onThoi,
}: {
  mat: readonly string[]
  dangLuu: boolean
  onVanLuu: () => void
  onThoi: () => void
}) {
  const thoi = dangLuu ? () => undefined : onThoi
  return (
    <To nhan={T.stopTitle} onDong={thoi}>
      <header className="to-dau">
        <h2>{T.stopTitle}</h2>
      </header>
      <div className="to-than">
        <ThongBao loai="canh-bao" lon>
          {cauHoiNgungNhan(mat)}
        </ThongBao>
      </div>
      <footer className="to-chan nghi-chan">
        <button type="button" className="nut nut-chinh nut-lon" disabled={dangLuu} onClick={onThoi}>
          {T.stopKeep}
        </button>
        <button
          type="button"
          className="nut nut-vien nut-lon"
          disabled={dangLuu}
          onClick={onVanLuu}
        >
          {dangLuu ? T.saving : T.stopSaveAnyway}
        </button>
      </footer>
    </To>
  )
}

/**
 * `chay(goi)` = `luuCoHoiLai` + tờ hỏi: trả `{ket:'luu', gia}` khi đã lưu (lần đầu hoặc sau "Vẫn lưu"), `{ket:'thoi'}` khi chọn "Thôi, để như cũ".
 * `hop` là phần tử phải được vẽ ở nơi dùng (tờ hiện khi đang hỏi). Lỗi khác 409 hỏi lại thì `chay` ném như thường.
 */
export function useHoiNgungNhan(): {
  chay: <R>(goi: (xacNhan: true | undefined) => Promise<R>) => Promise<KetQuaLuu<R>>
  hop: ReactNode
} {
  const [dangHoi, setDangHoi] = useState<{ mat: readonly string[]; dangLuu: boolean } | null>(null)
  const tra = useRef<((dongY: boolean) => void) | null>(null)
  const con = useRef(true)
  useEffect(() => {
    con.current = true
    return () => {
      con.current = false
      // Rời màn khi tờ đang mở = "Thôi" (không lưu gì thêm).
      tra.current?.(false)
    }
  }, [])

  const hoi = useCallback(
    (mat: readonly string[]) =>
      new Promise<boolean>((xong) => {
        tra.current = (dongY) => {
          tra.current = null
          // "Vẫn lưu": tờ GIỮ mở, hai nút khoá tới khi lưu xong. "Thôi": `chay` đóng tờ ở `finally`.
          if (dongY && con.current) setDangHoi((d) => (d === null ? d : { ...d, dangLuu: true }))
          xong(dongY)
        }
        setDangHoi({ mat, dangLuu: false })
      }),
    [],
  )
  const chay = useCallback(
    async <R,>(goi: (xacNhan: true | undefined) => Promise<R>) => {
      try {
        return await luuCoHoiLai(goi, hoi)
      } finally {
        if (con.current) setDangHoi(null)
      }
    },
    [hoi],
  )

  return {
    chay,
    hop:
      dangHoi === null ? null : (
        <HoiNgungNhan
          mat={dangHoi.mat}
          dangLuu={dangHoi.dangLuu}
          onVanLuu={() => tra.current?.(true)}
          onThoi={() => tra.current?.(false)}
        />
      ),
  }
}
